import { useState, useCallback, useEffect, useMemo } from "react";
import { useMountEffect, useUpdateEffect } from "../../../hooks/LifeCycle";
import { isObjectEmpty } from "../../../utils/ObjectUtil";
import {
  mapearRegistros,
  construirRequestParams,
  generarRecordKey,
} from "../utils/ValidacionesHojaQXScreenUtils";
import {
  INITIAL_FORM_DATA,
  ITEMS_PER_PAGE,
  ACTIONS_OPTIONS,
} from "../utils/ValidacionesHojaQXScreenConstants";

const useValidacionesHojaQXScreen = ({
  authUserData,
  consultarEstatusHojasQX,
  consultarEstatusHojasQXData,
  consultarEstatusHojasQXLoading,
  consultarEstatusHojasQXError,
  consultarTipoPssNoMedico,
  numeroIngresoInicial = null,
  autoBuscar = true,
}) => {
  const [formData, setFormData] = useState(
    numeroIngresoInicial
      ? { numeroIngreso: numeroIngresoInicial }
      : INITIAL_FORM_DATA
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [todosLosRegistrosMapeados, setTodosLosRegistrosMapeados] = useState([]);
  const [obteniendoTodasLasPaginas, setObteniendoTodasLasPaginas] = useState(false);
  const [todasLasValidaciones, setTodasLasValidaciones] = useState([]);
  const [formDataBusqueda, setFormDataBusqueda] = useState(null);
  const [alertConfig, setAlertConfig] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [visibleActionsMenu, setVisibleActionsMenu] = useState(null);
  const [showModalAnexos, setShowModalAnexos] = useState(false);
  const [anexosAMostrar, setAnexosAMostrar] = useState([]);
  const [modalDocumentoVisible, setModalDocumentoVisible] = useState(false);
  const [documentoModal, setDocumentoModal] = useState(null);
  const [showModalComentarios, setShowModalComentarios] = useState(false);
  const [comentariosAMostrar, setComentariosAMostrar] = useState(null);

  const mostrarAlerta = useCallback((type, title, message) => {
    setAlertConfig({
      type,
      title,
      message,
      onConfirm: () => setShowAlert(false),
    });
    setShowAlert(true);
  }, []);

  const obtenerCodigoPss = useCallback(() => {
    return authUserData?.prestador?.codigo;
  }, [authUserData]);

  const construirRequestData = useCallback(
    (data, pagina = 1) => {
      const codigoPss = obtenerCodigoPss();
      if (!codigoPss) return null;

      const requestParams = construirRequestParams(
        codigoPss,
        data?.numeroIngreso,
        data?.fechaInicio,
        data?.fechaFin,
        pagina,
        10
      );

      return { requestParams };
    },
    [obtenerCodigoPss]
  );

  const buscarRegistros = useCallback(
    async (page, data) => {
      setProcessing(true);
      setCurrentPage(1);
      setTodosLosRegistrosMapeados([]);
      setTodasLasValidaciones([]);
      setObteniendoTodasLasPaginas(true);
      setFormDataBusqueda(data);

      try {
        const codigoPss = obtenerCodigoPss();
        if (!codigoPss) {
          setProcessing(false);
          setObteniendoTodasLasPaginas(false);
          mostrarAlerta("error", "Error", "No se pudo obtener el código del PSS");
          return;
        }

        const consultarEstatusData = construirRequestData(data, 1);
        if (consultarEstatusData) {
          consultarEstatusHojasQX(consultarEstatusData);
        }
      } catch (error) {
        setProcessing(false);
        setObteniendoTodasLasPaginas(false);
        mostrarAlerta("error", "Error", "Error al consultar los registros");
      }
    },
    [obtenerCodigoPss, construirRequestData, consultarEstatusHojasQX, mostrarAlerta]
  );

  const onStateChange = useCallback(
    (key, format = null) =>
      (value) => {
        setFormData((prevState) => {
          const formattedValue = format ? format(value) : value;
          const newState = {
            ...prevState,
            [key]: formattedValue,
          };

          if (key === "fechaInicio") {
            newState.fechaFin = "";
          }

          return newState;
        });
      },
    []
  );

  const onCleanForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    buscarRegistros(1, INITIAL_FORM_DATA);
  }, [buscarRegistros]);

  const handleMenuClick = useCallback((action, record) => {
    setVisibleActionsMenu(null);

    if (action.value === "ver_anexos") {
      const anexos = record.anexos || [];
      setAnexosAMostrar(anexos);
      setShowModalAnexos(true);
    }

    if (action.value === "ver_comentarios") {
      setComentariosAMostrar(record);
      setShowModalComentarios(true);
    }
  }, []);

  const handleCloseModalAnexos = useCallback(() => {
    setShowModalAnexos(false);
    setAnexosAMostrar([]);
  }, []);

  const abrirModalDocumento = useCallback((anexo) => {
    const documento = {
      name: anexo.descripcionArchivo || "documento",
      url: anexo.rutaArchivo,
      type: null,
    };
    setDocumentoModal(documento);
    setModalDocumentoVisible(true);
  }, []);

  const cerrarModalDocumento = useCallback(() => {
    setModalDocumentoVisible(false);
    setDocumentoModal(null);
  }, []);

  const handleCloseModalComentarios = useCallback(() => {
    setShowModalComentarios(false);
    setComentariosAMostrar(null);
  }, []);

  useMountEffect(() => {
    if (autoBuscar) {
      buscarRegistros(1, formData);
    }
  });

  useEffect(() => {
    const codigoPss = authUserData?.prestador?.codigo;
    if (codigoPss && consultarTipoPssNoMedico) {
      consultarTipoPssNoMedico({ codigoPss });
    }
  }, [authUserData?.prestador?.codigo, consultarTipoPssNoMedico]);

  const procesarRegistrosMapeados = useCallback(
    async (validaciones) => {
      if (validaciones.length === 0) {
        setProcessing(false);
        setTodosLosRegistrosMapeados([]);
        setTotalRegistros(0);
        mostrarAlerta("info", "Búsqueda sin resultados", "No se encontraron registros.");
        return;
      }

      const registrosMapeados = await mapearRegistros(validaciones);
      setTodosLosRegistrosMapeados(registrosMapeados);
      setTotalRegistros(registrosMapeados.length);
      setProcessing(false);
    },
    [mostrarAlerta]
  );

  useEffect(() => {
    const procesarRespuesta = async () => {
      if (
        !isObjectEmpty(consultarEstatusHojasQXData) &&
        obteniendoTodasLasPaginas &&
        !consultarEstatusHojasQXLoading
      ) {
        const estatusCoberturas =
          consultarEstatusHojasQXData?.data?.estatusCoberturas || [];
        const paginacion = consultarEstatusHojasQXData?.paginacion || null;

        const nuevasValidaciones = [...todasLasValidaciones, ...estatusCoberturas];
        setTodasLasValidaciones(nuevasValidaciones);

        const totalPaginas = paginacion?.totalPaginas || 1;
        const paginaActual = paginacion?.pagina || 1;

        if (paginaActual < totalPaginas) {
          const consultarEstatusData = construirRequestData(
            formDataBusqueda,
            paginaActual + 1
          );
          if (consultarEstatusData) {
            consultarEstatusHojasQX(consultarEstatusData);
          }
        } else {
          setObteniendoTodasLasPaginas(false);
          await procesarRegistrosMapeados(nuevasValidaciones);
        }
      }
    };

    procesarRespuesta();
  }, [
    consultarEstatusHojasQXData,
    consultarEstatusHojasQXLoading,
    obteniendoTodasLasPaginas,
    todasLasValidaciones,
    formDataBusqueda,
    construirRequestData,
    consultarEstatusHojasQX,
    procesarRegistrosMapeados,
  ]);

  const registros = useMemo(() => {
    if (todosLosRegistrosMapeados.length === 0) return [];
    const inicio = (currentPage - 1) * ITEMS_PER_PAGE;
    const fin = inicio + ITEMS_PER_PAGE;
    return todosLosRegistrosMapeados.slice(inicio, fin);
  }, [currentPage, todosLosRegistrosMapeados]);

  useUpdateEffect(() => {
    if (!isObjectEmpty(consultarEstatusHojasQXError) && processing) {
      setTodosLosRegistrosMapeados([]);
      mostrarAlerta(
        "error",
        "Error",
        consultarEstatusHojasQXError?.mensaje || "Error al obtener los registros"
      );
      setProcessing(false);
    }
  }, [consultarEstatusHojasQXError, processing, mostrarAlerta]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  return {
    formData,
    currentPage,
    totalRegistros,
    processing,
    registros,
    alertConfig,
    showAlert,
    visibleActionsMenu,
    showModalAnexos,
    anexosAMostrar,
    modalDocumentoVisible,
    documentoModal,
    showModalComentarios,
    comentariosAMostrar,
    actionsOptions: ACTIONS_OPTIONS,
    itemsPerPage: ITEMS_PER_PAGE,
    onStateChange,
    onCleanForm,
    buscarRegistros,
    handlePageChange,
    handleMenuClick,
    handleCloseModalAnexos,
    abrirModalDocumento,
    cerrarModalDocumento,
    handleCloseModalComentarios,
    setVisibleActionsMenu,
    generarRecordKey,
  };
};

export default useValidacionesHojaQXScreen;

